import * as store from "../state/store.js";
import type {
  ContextMenuState,
  ModalKind,
  ModalState,
  Theme,
} from "../state/types.js";
import { centerHorizontally, fitToBounds } from "./view.js";

// Toast: persistent bottom-right queue. Entries stay until the user clicks
// their close button. Newest entries are pushed to the front so the visible
// stack always shows the freshest message on top.
const MAX_TOAST_HISTORY = 50;

export function toast(message: string): void {
  const entry = {
    id: store.peek.nextToastId(),
    message,
    timestamp: Date.now(),
  };
  const current = store.peek.toasts();
  // Prepend + trim to keep the queue bounded — a runaway op should never be
  // able to grow the DOM unboundedly.
  const next = [entry, ...current].slice(0, MAX_TOAST_HISTORY);
  store.write.toasts(next);
}

export function dismissToast(id: number): void {
  store.write.toasts(store.peek.toasts().filter((t) => t.id !== id));
}

export function dismissAllToasts(): void {
  store.write.toasts([]);
  store.write.toastsExpanded(false);
}

export function toggleToastStack(): void {
  store.write.toastsExpanded(!store.toastsExpanded());
}

// Modal: a discriminated payload driven by `kind`. Components pick the right
// modal to render based on the kind.
export function openModal(kind: ModalKind, data: Record<string, unknown> = {}): void {
  const next: ModalState = { open: true, kind, data };
  store.write.modal(next);
}

export function closeModal(): void {
  store.write.modal({ open: false, kind: null, data: {} });
}

// Convenience wrapper for the generic yes/cancel confirmation modal.
// Callers pass a title, message, and the action to run on confirm; the
// modal handles Cancel/close and Enter/Esc key routing.
export interface ConfirmOptions {
  title: string;
  message?: string;
  confirmLabel?: string;
  cancelLabel?: string;
  danger?: boolean;
  onConfirm: () => void;
}

export function confirmDialog(opts: ConfirmOptions): void {
  openModal("confirm", { ...opts });
}

// Context menu at a given viewport position. Pass `layer` to open the
// layer-scoped variant (Select All / Deselect All within that layer);
// omit for the general canvas context menu.
export function openContextMenu(x: number, y: number, layer?: string): void {
  const state: ContextMenuState = { open: true, x, y };
  if (layer !== undefined) state.layer = layer;
  store.write.contextMenu(state);
}

export function closeContextMenu(): void {
  store.write.contextMenu({ open: false, x: 0, y: 0 });
}

// Theme
export function toggleTheme(): void {
  store.write.theme(store.theme() === "dark" ? "light" : "dark");
}

export function setTheme(v: Theme): void {
  store.write.theme(v);
}

// Sidebar
export function toggleSidebar(): void {
  store.write.sidebarVisible(!store.sidebarVisible());
  // The canvas is about to grow or shrink as the panel collapses/expands.
  // Wait two frames for the ResizeObserver to publish the new canvas size,
  // then re-anchor the drawing to the horizontal center of the new viewport.
  if (!store.isLoaded()) return;
  requestAnimationFrame(() => {
    requestAnimationFrame(() => centerHorizontally());
  });
}

export function toggleSidebarPosition(): void {
  store.write.sidebarPosition(
    store.sidebarPosition() === "left" ? "right" : "left"
  );
}

// Background grid lines (world-anchored horizontal + vertical lines)
export function toggleGrid(): void {
  store.write.showGrid(!store.showGrid());
  toast(store.showGrid() ? "Grid shown" : "Grid hidden");
}

// Grid ruler strips along the top + left of the canvas
export function toggleGridRuler(): void {
  store.write.showGridRuler(!store.showGridRuler());
  toast(store.showGridRuler() ? "Grid ruler shown" : "Grid ruler hidden");
}

// 2D canvas <-> 3D GLB preview. Callable while glbUrl is null (no-op) —
// the topbar guard also disables the 3D button until the GLB build finishes,
// so this is defensive.
export function toggleViewMode(): void {
  if (!store.glbUrl() && store.viewMode() === "2d") return;
  store.write.viewMode(store.viewMode() === "2d" ? "3d" : "2d");
}

export function setViewMode(mode: "2d" | "3d"): void {
  if (mode === store.viewMode()) return;
  if (mode === "3d" && !store.glbUrl()) return;
  store.write.viewMode(mode);
}

// Fit / reset for whichever view is currently active. In 2D that means the
// existing fitToBounds; in 3D we bump a signal the view-3d component watches
// and it re-frames its camera. Wired to the topbar's Fit button so a single
// icon works across both modes.
export function fitCurrentView(): void {
  if (store.viewMode() === "3d") {
    store.write.bumpReset();
  } else {
    if (!store.bounds()) {
      toast("No DXF loaded");
      return;
    }
    fitToBounds();
  }
}
